import { Modal, Form, Select, Button, Typography, Spin, Alert, Descriptions } from 'antd';

import { IInvestimentoACadastrar } from '../../interfaces';

import { useInvestimentoFormModal } from './useInvestimentoFormModal';
import { convertMoedaBRDoJSComExtamente2CasasDecimais } from '../../../../../services/Conversores';
import { removeAcento } from '../../../../../utils/RemoveAcentos';

const { Text } = Typography;

interface IProps {
    visible: boolean;
    investimento: IInvestimentoACadastrar | null;
    onClose: () => void;
    onInvestimentoCreated: (investimentoACadastrarId: number) => void;
}

export default function InvestimentoFormModal({
    visible,
    investimento,
    onClose,
    onInvestimentoCreated,
}: IProps) {
    const {
        form,
        loadingFontes,
        submitting,
        fontes,
        fonteSelecionadaNumero,
        contasDisponiveis,
        handleChangeFonte,
        totalContas,
        handleFinish,
    } = useInvestimentoFormModal({
        visible,
        investimento,
        onClose,
        onInvestimentoCreated,
    });

    return (
        <Modal
            title="Cadastrar investimento"
            open={visible}
            onCancel={onClose}
            destroyOnClose
            footer={null}
            width={650}
        >
            {investimento && (
                <div style={{ marginBottom: 20 }}>
                    <Descriptions size="small" bordered column={1}>
                        <Descriptions.Item label="Nome">
                            <Text strong>{investimento.nome}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Valor">
                            {`R$ ${convertMoedaBRDoJSComExtamente2CasasDecimais(investimento.valor, true)}`}
                        </Descriptions.Item>
                        <Descriptions.Item label="Mês / ano">
                            {`${String(investimento.mes).padStart(2, '0')}/${investimento.ano}`}
                        </Descriptions.Item>
                        <Descriptions.Item label="Banco">
                            {investimento.banco?.nome || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Órgão">
                            {investimento.orgaoVinculado?.orgao?.nome || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Órgão vinculado">
                            {investimento.orgaoVinculado?.nome || '-'}
                        </Descriptions.Item>
                    </Descriptions>
                </div>
            )}

            <Spin spinning={loadingFontes}>
                {!loadingFontes && fontes.length > 0 && totalContas === 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        message="As fontes disponíveis não possuem contas vinculadas."
                        style={{ marginBottom: 16 }}
                    />
                )}

                {!loadingFontes && fontes.length === 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        message="Nenhuma fonte ou conta encontrada para este investimento."
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleFinish}
                >
                    <Form.Item
                        name="fonteNumero"
                        label="Fonte"
                        rules={[{ required: true, message: 'Por favor, selecione a fonte!' }]}
                    >
                        <Select
                            placeholder="Selecione a fonte"
                            showSearch
                            allowClear
                            optionLabelProp="title"
                            onChange={handleChangeFonte}
                            filterOption={(input: any, option: any) => {
                                let textDigit = removeAcento(input);
                                let searchableText = removeAcento(option?.searchValue || '');
                                return searchableText.indexOf(textDigit) >= 0;
                            }}
                            options={fontes.map((e) => ({
                                value: e.numero,
                                title: `${e.numero} - ${e.descricao}`,
                                searchValue: `${e.numero} - ${e.descricao} ${e.categoria?.nome || ''}`,
                                label: (
                                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, padding: '2px 0' }}>
                                        <span style={{ fontWeight: 500 }}>{e.numero} - {e.descricao}</span>
                                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                            {e.categoria?.nome}
                                        </Typography.Text>
                                    </div>
                                ),
                            }))}
                            disabled={loadingFontes || fontes.length === 0}
                        />
                    </Form.Item>

                    <Form.Item
                        name="contaId"
                        label="Conta bancária"
                        rules={[{ required: true, message: 'Por favor, selecione a conta!' }]}
                    >
                        <Select
                            placeholder={
                                !fonteSelecionadaNumero
                                    ? 'Selecione primeiro uma fonte'
                                    : contasDisponiveis.length === 0
                                        ? 'Nenhuma conta vinculada a esta fonte'
                                        : 'Selecione a conta'
                            }
                            showSearch
                            allowClear
                            optionLabelProp="title"
                            filterOption={(input: any, option: any) => {
                                let textDigit = removeAcento(input);
                                let searchableText = removeAcento(option?.searchValue || '');
                                return searchableText.indexOf(textDigit) >= 0;
                            }}
                            options={contasDisponiveis.map((conta) => ({
                                value: conta.id,
                                title: `Agência: ${conta.agencia} | Conta: ${conta.numero}${conta.descricao ? ` - ${conta.descricao}` : ''}`,
                                searchValue: `${conta.agencia} ${conta.numero} ${conta.descricao || ''}`,
                                label: (
                                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, padding: '2px 0' }}>
                                        <span style={{ fontWeight: 500 }}>Agência: {conta.agencia} | Conta: {conta.numero}</span>
                                        {conta.descricao && (
                                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                {conta.descricao}
                                            </Typography.Text>
                                        )}
                                    </div>
                                ),
                            }))}
                            disabled={!fonteSelecionadaNumero || contasDisponiveis.length === 0}
                        />
                    </Form.Item>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24, gap: 8 }}>
                        <Button onClick={onClose} disabled={submitting}>
                            Cancelar
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            className="btn_success_vlhbegoawrgbvr"
                            loading={submitting}
                            disabled={loadingFontes || !fonteSelecionadaNumero || contasDisponiveis.length === 0}
                        >
                            Cadastrar
                        </Button>
                    </div>
                </Form>
            </Spin>
        </Modal>
    );
}
